import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Box, Breadcrumbs, Typography } from '@mui/material';
import { useResponsiveCC } from 'hooks';
import { useTranslation } from 'react-i18next';
import { Link, useMatches } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setCurrentTab } from 'pages/access-log/store/accessLogSlice';
import { ARRAY_TABS_LOG, TABS_LOG } from 'pages/access-log/constant';
import theme, { fontTypo } from 'theme';
import IconBreadcrumb from './breadcrumbs/IconBreadcrumb';
import { BREADCRUMBS } from './breadcrumbs/const';
import { PATH_ROOT } from 'constants/api';
import { isDefaultColorSystem } from 'utils/functions';

const ACCESS_LOG_TAB_BY_CRUMB = {
    'history-mfa': TABS_LOG.HISTORY_MFA,
};

const normalizeCrumbs = (match) => {
    const handle = match.handle || {};
    const crumbs = handle.crumbs || handle.crumb;

    if (!crumbs) {
        return [];
    }

    return (Array.isArray(crumbs) ? crumbs : [crumbs])
        .filter(Boolean)
        .map((item) => {
            if (typeof item === 'string') {
                return {
                    crumb: item,
                    params: handle.params,
                };
            }

            return {
                ...item,
                params: item.params || handle.params,
            };
        })
        .filter((item) => Boolean(item.crumb));
};

const getCrumbPath = (to) => {
    if (!to) {
        return '';
    }

    return to.startsWith('/') ? to : `${PATH_ROOT}/${to}`;
};

/**
 * Breadcrumb theo url của hệ thống
 * @returns
 */
const BreadcrumbsDynamicCC = ({ namePackage }) => {
    const { t } = useTranslation();
    const { isMobile } = useResponsiveCC();
    const color = isDefaultColorSystem()
        ? {
            textColor: '#44494d',
            textHover: '#000',
            iconFill: '#606060',
        }
        : {
            textColor: 'text[700]',
            textHover: 'primary.main',
            iconFill: 'primary.main',
        };

    const matches = useMatches();
    const dispatch = useDispatch();

    const handleClickBreadcrumb = (crumb) => {
        const tab = ACCESS_LOG_TAB_BY_CRUMB[crumb];
        const tabIndex = ARRAY_TABS_LOG.indexOf(tab);

        if (tabIndex >= 0) {
            dispatch(setCurrentTab(tabIndex));
        }
    };

    const breadcrumbs = matches.flatMap((match) => {
        const crumbs = normalizeCrumbs(match);

        return crumbs.map((item, index) => ({
            ...item,
            key: `${match.id || match.pathname}-${item.crumb}-${index}`,
            pathname: getCrumbPath(item.to) || (index === crumbs.length - 1 ? match.pathname : ''),
            isCustomPath: Boolean(item.to),
            match,
        }));
    });

    const crumbs2 = breadcrumbs.map((item, index) => {
        const { crumb, params, pathname, match, labelKey, isCustomPath } = item;
        const isActive = index === breadcrumbs.length - 1;
        const label = labelKey ? t(labelKey) : (BREADCRUMBS[crumb] ? t(BREADCRUMBS[crumb]) : namePackage || crumb);
        const paramValue = isCustomPath && params ? match.params?.[params] : '';
        const to = pathname
            ? `${pathname}${paramValue ? '/' + paramValue : ''}`
            : '';

        if (isActive && index > 0) {
            return (
                <Box
                    key={item.key}
                    sx={{ textDecoration: 'unset', color: (theme) => theme.palette.text[700] }}
                >
                    <Typography
                        sx={{
                            fontSize: fontTypo.title,
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing(1.5),
                        }}
                    >
                        <IconBreadcrumb crumb={crumb} /> {label}
                    </Typography>
                </Box>
            );
        }

        if (isActive && index === 0) {
            return (
                <Box
                    key={item.key}
                    style={{ textDecoration: 'unset' }}
                    onClick={() => {
                        window.location.reload();
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: fontTypo.title,
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing(1.5),
                            textWrap: 'nowrap',
                            cursor: 'pointer',
                            color: color.textColor,
                            ':hover': {
                                transform: 'scale(1.025)',
                                color: color.textHover,
                                '& svg path': {
                                    fill: color.iconFill,
                                },
                            }
                        }}
                    >
                        <IconBreadcrumb crumb={crumb} />
                        {label}
                    </Typography>
                </Box>
            );
        }

        if (!isMobile && to) {
            return (
                <Link
                    key={item.key}
                    to={to}
                    style={{ textDecoration: 'unset' }}
                    onClick={() => handleClickBreadcrumb(crumb)}
                >
                    <Typography
                        sx={{
                            fontSize: fontTypo.title,
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing(1.5),
                            textWrap: 'nowrap',
                            cursor: 'pointer',
                            ':hover': { transform: 'scale(1.025)' },
                        }}
                    >
                        <IconBreadcrumb crumb={crumb} /> {label}
                    </Typography>
                </Link>
            );
        }

        if (!isMobile) {
            return (
                <Box
                    key={item.key}
                    sx={{ textDecoration: 'unset' }}
                >
                    <Typography
                        sx={{
                            fontSize: fontTypo.title,
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing(1.5),
                            textWrap: 'nowrap',
                        }}
                    >
                        <IconBreadcrumb crumb={crumb} /> {label}
                    </Typography>
                </Box>
            );
        }

        return null;
    });

    return (
        <Breadcrumbs separator={<NavigateNextIcon fontSize="medium" />} aria-label="breadcrumb">
            {crumbs2}
        </Breadcrumbs>
    );
};

export default BreadcrumbsDynamicCC;
